<template>
  <div>
    <InputLabelBase
      v-if="label"
      :label="label"
      :tool-tip="toolTip"
      :helper-text="helperText"
      :description="description"
      :small="small"
      :class="{ 'sr-only': hideLabel }"
    />
    <div class="relative">
      <button
        :id="label ? labelCleanUp(label) : ''"
        type="button"
        class="block w-full rounded-md border-none bg-white text-left shadow-inner outline-none ring-1 ring-inset placeholder:text-grey-500 focus:text-grey-900 focus:ring-2 active:text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
        :class="[
          small ? 'px-2 py-1.5 text-sm' : 'px-3 py-2 text-base',

          error ? 'ring-rose-700' : 'ring-grey-400 focus:ring-green-700',
        ]"
        :name="label ? labelCleanUp(label) : ''"
        @click="toggleVisibility"
        @keydown.esc.exact="hideDropdown"
        @keydown.shift.tab="hideDropdown"
        @keydown.up.exact.prevent="startArrowKeys"
        @keydown.down.exact.prevent="startArrowKeys"
      >
        <span class="flex items-center justify-between">
          {{ selectedOption ?? placeholder ?? "Select..." }}
          <IconChevronDown class="h-4 w-4 shrink-0 fill-grey-500" />
        </span>
      </button>

      <Transition>
        <ol
          v-if="selectOpen"
          ref="optionsList"
          role="listbox"
          class="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-2 shadow-lg ring-1 ring-inset ring-grey-400 focus:outline-none"
        >
          <li
            v-for="(option, index) in options"
            :key="option.value"
            ref="optionItems"
            role="option"
            tabindex="0"
            class="mr-0.5 flex items-center justify-between border-l-2 text-sm text-grey-900 hover:cursor-pointer hover:bg-grey-100 focus:border-grey-400 focus:bg-grey-100 focus:outline-0"
            :class="[
              (multiSelect &&
                activeItems.some((item) => item.value === option.value)) ||
              (!multiSelect && option.value === model)
                ? 'border-l-green-800 hover:border-green-800'
                : 'border-transparent hover:border-grey-400',
              small ? 'px-2 py-1.5' : 'px-4 py-3',
            ]"
            @click="handleSelect(option.value, option.text)"
            @keydown.esc.exact="hideDropdown"
            @keydown.tab.exact="
              options.length - 1 === index ? hideDropdown() : focusNext(false)
            "
            @keydown.shift.tab="
              index === 0 ? hideDropdown() : focusPrevious(false)
            "
            @keydown.enter.exact="handleSelect(option.value, option.text)"
            @keydown.space.exact.prevent="
              handleSelect(option.value, option.text)
            "
            @keydown.up.exact.prevent="index !== 0 && focusPrevious(true)"
            @keydown.down.exact.prevent="
              options.length - 1 !== index && focusNext(true)
            "
          >
            {{ option.text }}
            <div v-if="multiSelect" class="relative">
              <input
                class="peer mt-1 h-5 w-5 shrink-0 appearance-none rounded border-2 border-grey-400 bg-white checked:border checked:border-green-800 checked:bg-green-800 focus:outline-none"
                type="checkbox"
                tabindex="-1"
                :checked="
                  activeItems.some((item) => item.value === option.value)
                "
              />
              <IconCheck
                class="absolute left-1/2 top-1/2 hidden h-4 w-4 -translate-x-1/2 -translate-y-1/2 transform fill-white outline-none peer-checked:block"
              />
            </div>
          </li>
        </ol>
      </Transition>
      <div v-if="activeItems.length > 0" class="flex flex-wrap gap-2 pt-2">
        <div
          v-for="option in activeItems"
          :key="option.value"
          class="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-green-100 py-1 pl-2 pr-3 hover:bg-green-300"
          @click="removeItem(option)"
        >
          <span
            class="text-xs font-bold uppercase tracking-[1.2px] text-grey-900"
            >{{ option.text }}</span
          >
          <IconToggleCross class="size-2 fill-green-800" />
        </div>
      </div>
      <p
        v-if="error || hint"
        class="mb-0 mt-1 text-sm"
        :class="error ? 'text-rose-700' : 'text-grey-600'"
      >
        {{ error || hint }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { labelCleanUp } from "@/utils/formValidation";

const model = defineModel<string | number | (string | number)[] | undefined>();
const props = defineProps<{
  label?: string;
  hideLabel?: boolean;
  toolTip?: string;
  helperText?: string;
  description?: string;
  placeholder?: string;
  small?: boolean;
  error?: string;
  hint?: string;
  multiSelect?: boolean;
  options: {
    value: string | number;
    text: string;
  }[];
}>();

const emit = defineEmits(["change"]);

const selectOpen = ref(false);
const selectedOption = ref<string | null>(null);
const activeItems = ref<{ value: string | number; text: string }[]>([]);
const optionItems = ref<HTMLElement[]>([]);
const optionsList = ref<HTMLElement | null>(null);
const focusedIndex = ref(0);
activeItems.value = props.options.filter((item) => {
  if (Array.isArray(model.value)) {
    return model.value.includes(item.value);
  }
});

const toggleVisibility = () => {
  selectOpen.value = !selectOpen.value;
};

const hideDropdown = () => {
  selectOpen.value = false;
};

const startArrowKeys = () => {
  if (selectOpen.value) {
    optionItems.value[0].focus();
  }
};

const focusPrevious = (isArrowKey: boolean) => {
  focusedIndex.value = Math.max(0, focusedIndex.value - 1);

  if (isArrowKey && optionItems.value[focusedIndex.value]) {
    optionItems.value[focusedIndex.value].focus();
  }
};

const focusNext = (isArrowKey: boolean) => {
  focusedIndex.value = Math.min(
    optionItems.value.length - 1,
    focusedIndex.value + 1,
  );

  if (isArrowKey && optionItems.value[focusedIndex.value]) {
    optionItems.value[focusedIndex.value].focus();
  }
};

const handleSelect = (value: string | number, text: string) => {
  if (props.multiSelect) {
    if (activeItems.value.some((item) => item.value === value)) {
      activeItems.value = activeItems.value.filter(
        (item) => item.value !== value,
      );
      model.value = activeItems.value.map((item) => item.value);
      return;
    }
    activeItems.value = [...activeItems.value, { value, text }];
    model.value = activeItems.value.map((item) => item.value);
    emit("change", model.value);
    return;
  } else {
    model.value = value;
    selectOpen.value = false;
    emit("change", model.value);

    return;
  }
  emit("change", model.value);
};

watch(model, (newVal) => {
  selectedOption.value = props.options.find((option) => option.value === newVal)
    ?.text as string | null;
});

const handleClickOutside = (e: MouseEvent) => {
  const dropdownElement = optionsList.value;
  const buttonElement = e.target as HTMLElement;

  if (dropdownElement && !dropdownElement.contains(buttonElement)) {
    hideDropdown();
  }
};

const handleEscapeKey = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    hideDropdown();
  }
};

onMounted(() => {
  if (model.value) {
    selectedOption.value = props.options.find(
      (option) => option.value === model.value,
    )?.text as string | null;
  }

  document.addEventListener("click", handleClickOutside, true);
  document.addEventListener("keydown", handleEscapeKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside, true);
  document.removeEventListener("keydown", handleEscapeKey);
});

const removeItem = (item: { value: string | number; text: string }) => {
  activeItems.value = activeItems.value.filter(
    (activeItem) => activeItem.value !== item.value,
  );
  model.value = activeItems.value.map((item) => item.value);
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: all 0.1s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
